<template>
  <div
    class="leave-group-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="leave-group-modal-title"
    aria-describedby="leave-group-modal-description"
    @click.self="closePopup"
  >
    <div
      ref="dialog"
      class="leave-group-modal__dialog"
    >
      <div class="leave-group-modal__content">
        <div
          class="leave-group-modal__icon"
          aria-hidden="true"
        >
          <i class="fa fa-sign-out"></i>
        </div>

        <p
          id="leave-group-modal-description"
          class="leave-group-modal__message"
        >
          Are you sure you want to exit
          <strong>{{ formattedGroupName }}</strong>?
        </p>

        <p class="leave-group-modal__description">
          You will no longer receive messages or notifications
          from this group.
        </p>

        <p
          v-if="errorMessage"
          class="leave-group-modal__error"
          role="alert"
        >
          <i
            class="fa fa-exclamation-circle"
            aria-hidden="true"
          ></i>

          {{ errorMessage }}
        </p>
      </div>

      <footer class="leave-group-modal__footer">
        <button
          type="button"
          class="
            leave-group-modal__button
            leave-group-modal__button--cancel
          "
          :disabled="isLeaving"
          @click="closePopup"
        >
          Cancel
        </button>

        <button
          ref="exitButton"
          type="button"
          class="
            leave-group-modal__button
            leave-group-modal__button--exit
          "
          :disabled="isLeaving || !hasValidRoomId"
          @click="leaveGroup"
        >
          <i
            v-if="isLeaving"
            class="fa fa-spinner fa-spin"
            aria-hidden="true"
          ></i>

          <i
            v-else
            class="fa fa-sign-out"
            aria-hidden="true"
          ></i>

          <span>
            {{ isLeaving ? 'Exiting...' : 'Exit Group' }}
          </span>
        </button>
      </footer>
    </div>
  </div>
</template>

<script>
import { bus } from '@/main';
import toast from '@/services/toast';

const BODY_SCROLL_CLASS =
  'body_right_scroll_remove';

export default {
  name: 'UserLeaveGroupPopup',

  props: {
    groupInfo: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      isLeaving: false,
      errorMessage: '',
      previouslyFocusedElement: null,
      listenersRemoved: false
    };
  },

  computed: {
    groupName() {
      if (!this.groupInfo) {
        return '';
      }

      const name =
        this.groupInfo.group_name ||
        this.groupInfo.name ||
        this.groupInfo.room_name ||
        '';

      return String(name).trim();
    },

    formattedGroupName() {
      return this.groupName
        ? '"' + this.groupName + '"'
        : 'this group';
    },

    roomId() {
      if (!this.groupInfo) {
        return null;
      }

      if (
        this.groupInfo.chat_room !== undefined &&
        this.groupInfo.chat_room !== null
      ) {
        return this.groupInfo.chat_room;
      }

      if (
        this.groupInfo.room_id !== undefined &&
        this.groupInfo.room_id !== null
      ) {
        return this.groupInfo.room_id;
      }

      if (
        this.groupInfo.id !== undefined &&
        this.groupInfo.id !== null
      ) {
        return this.groupInfo.id;
      }

      return null;
    },

    hasValidRoomId() {
      return (
        this.roomId !== null &&
        this.roomId !== undefined &&
        String(this.roomId).trim() !== ''
      );
    }
  },

  mounted() {
    this.previouslyFocusedElement =
      document.activeElement;

    document.body.classList.add(
      BODY_SCROLL_CLASS
    );

    document.addEventListener(
      'keydown',
      this.handleKeydown
    );

    this.$nextTick(() => {
      if (this.$refs.exitButton) {
        this.$refs.exitButton.focus();
      }
    });
  },

  beforeDestroy() {
    this.removeGlobalListeners();
  },

  methods: {
    async leaveGroup() {
      if (this.isLeaving) {
        return;
      }

      if (!this.hasValidRoomId) {
        this.errorMessage =
          'Group room information is unavailable.';

        return;
      }

      this.isLeaving = true;
      this.errorMessage = '';

      try {
        const response =
          await this.$store.dispatch(
            'leaveGroupRoomById',
            {
              room_id: this.roomId
            }
          );

        /*
         * Clear the active group and its cached messages
         * only after the leave request succeeds.
         */
        bus.$emit(
          'roomClearedBus',
          this.roomId
        );

        /*
         * Refresh failures are handled internally because
         * leaving the group has already succeeded.
         */
        await this.refreshRoomLists();

        toast.success(
          this.getSuccessMessage(
            response,
            'You have exited the group successfully.'
          )
        );

        this.closePopup(true);
      } catch (error) {
        this.errorMessage =
          this.getErrorMessage(
            error,
            'Unable to exit the group. Please try again.'
          );

        console.error(
          'Unable to leave group:',
          error
        );
      } finally {
        this.isLeaving = false;
      }
    },

    async refreshRoomLists() {
      const refreshRequests = [
        this.refreshPinnedRooms(),
        this.refreshNormalRooms()
      ];

      await Promise.all(refreshRequests);
    },

    async refreshPinnedRooms() {
      try {
        await this.$store.dispatch(
          'fetchPinnedChatRooms'
        );
      } catch (error) {
        console.error(
          'Unable to refresh pinned rooms:',
          error
        );
      }
    },

    async refreshNormalRooms() {
      try {
        await this.$store.dispatch(
          'fetchNormalChatRooms'
        );
      } catch (error) {
        console.error(
          'Unable to refresh normal rooms:',
          error
        );
      }
    },

    closePopup(forceClose) {
      if (
        this.isLeaving &&
        forceClose !== true
      ) {
        return;
      }

      this.removeGlobalListeners();

      bus.$emit(
        'leaveGroupPopupStateBus',
        false
      );

      this.restoreFocus();
    },

    handleKeydown(event) {
      if (!event) {
        return;
      }

      if (
        event.key === 'Escape' &&
        !this.isLeaving
      ) {
        this.closePopup();
      }
    },

    removeGlobalListeners() {
      if (this.listenersRemoved) {
        return;
      }

      document.body.classList.remove(
        BODY_SCROLL_CLASS
      );

      document.removeEventListener(
        'keydown',
        this.handleKeydown
      );

      this.listenersRemoved = true;
    },

    restoreFocus() {
      const previousElement =
        this.previouslyFocusedElement;

      this.$nextTick(() => {
        if (
          previousElement &&
          document.body.contains(previousElement) &&
          typeof previousElement.focus ===
            'function'
        ) {
          previousElement.focus();
        }

        this.previouslyFocusedElement =
          null;
      });
    },

    getSuccessMessage(
      response,
      fallbackMessage
    ) {
      if (!response || !response.data) {
        return fallbackMessage;
      }

      const responseData = response.data;

      return (
        responseData.user_msg ||
        responseData.msg ||
        responseData.message ||
        fallbackMessage
      );
    },

    getErrorMessage(
      error,
      fallbackMessage
    ) {
      const responseData =
        error &&
        error.response &&
        error.response.data
          ? error.response.data
          : error && error.data
            ? error.data
            : null;

      if (!responseData) {
        return error && error.message
          ? error.message
          : fallbackMessage;
      }

      const nestedData =
        responseData.data &&
        typeof responseData.data ===
          'object'
          ? responseData.data
          : null;

      const nestedErrors =
        nestedData &&
        Array.isArray(
          nestedData.non_field_errors
        )
          ? nestedData.non_field_errors
          : [];

      if (nestedErrors.length) {
        return nestedErrors[0];
      }

      const directErrors =
        Array.isArray(
          responseData.non_field_errors
        )
          ? responseData.non_field_errors
          : [];

      if (directErrors.length) {
        return directErrors[0];
      }

      if (
        responseData.detail &&
        typeof responseData.detail ===
          'string'
      ) {
        return responseData.detail;
      }

      return (
        responseData.user_msg ||
        responseData.msg ||
        responseData.message ||
        fallbackMessage
      );
    }
  }
};
</script>

<style lang="scss" scoped>
$overlay-background: rgba(0, 0, 0, 0.92);
$dialog-background: #000000;
$primary-text: #ffffff;
$secondary-text: #aaaaaa;
$accent-color: #ffd500;
$hover-color: #ffe738;
$danger-color: #ff4a43;
$error-color: #ff716b;
$border-color: rgba(255, 255, 255, 0.12);

.leave-group-modal {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  overflow-y: auto;
  background-color: $overlay-background;
  font-family:
    'MyriadProRegular',
    'Lato',
    sans-serif;

  &__dialog {
    width: 100%;
    max-width: 500px;
    overflow: hidden;
    border-radius: 6px;
    color: $primary-text;
    background-color: $dialog-background;
    box-shadow: 0 0 13px
      rgba(255, 213, 0, 0.6);
  }

  &__content {
    padding: 26px 20px 22px;
    text-align: center;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    margin-bottom: 16px;
    border-radius: 50%;
    color: $danger-color;
    background-color: rgba(
      255,
      74,
      67,
      0.12
    );
    font-size: 28px;
  }

  &__message {
    margin: 0;
    color: $primary-text;
    font-size: 18px;
    line-height: 1.5;
    overflow-wrap: anywhere;

    strong {
      color: #87cefa;
      font-weight: 600;
    }
  }

  &__description {
    margin: 10px 0 0;
    color: $secondary-text;
    font-size: 14px;
    line-height: 1.5;
  }

  &__error {
    margin: 18px 0 0;
    padding: 10px 12px;
    border: 1px solid rgba(
      255,
      113,
      107,
      0.35
    );
    border-radius: 4px;
    color: $error-color;
    background-color: rgba(
      255,
      113,
      107,
      0.08
    );
    font-size: 14px;
    line-height: 1.4;
    text-align: left;

    .fa {
      margin-right: 6px;
    }
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 0 20px 20px;
  }

  &__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 120px;
    min-height: 40px;
    padding: 9px 14px;
    border: 0;
    border-radius: 4px;
    color: $primary-text;
    font-family: inherit;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    box-shadow: none;
    transition:
      color 0.2s ease,
      background-color 0.2s ease,
      opacity 0.2s ease;

    .fa {
      margin-right: 7px;
    }

    &:hover:not(:disabled){
      color: #22292f;
      background-color: $hover-color;
    }

    &:focus {
      outline: 2px solid $accent-color;
      outline-offset: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }

    &--cancel {
      background-color: rgba(
        255,
        255,
        255,
        0.14
      );
    }

    &--exit {
      background-color: rgba(
        255,
        74,
        67,
        0.55
      );
    }
  }
}

@media only screen and (max-width: 575px) {
  .leave-group-modal {
    align-items: flex-end;
    padding: 10px;

    &__dialog {
      max-width: 100%;
    }

    &__header {
      padding: 16px;
    }

    &__content {
      padding: 22px 16px;
    }

    &__footer {
      flex-direction: column-reverse;
      padding: 0 16px 16px;
    }

    &__button {
      width: 100%;
    }
  }
}
</style>